import { Users2 } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiService } from '../../services/api';
import { Pencil, Trash2, ClipboardList, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const emptyGoalEvent = () => ({
  player_name: '',
  team: 'home',
  minute: '',
  assist_by: 'N/A',
  type: 'goal',
});

const AdminMatches = () => {
    // v2: fix TDZ - filteredMatches moved after displayedMatches
    // Lineup modal state
    const [showLineupModal, setShowLineupModal] = useState(false);
    const [lineupMatch, setLineupMatch] = useState(null);
    const [lineupTeam, setLineupTeam] = useState('');
    const [lineupPlayers, setLineupPlayers] = useState([]);
    const [lineupSubs, setLineupSubs] = useState([]);
    const [lineupLoading, setLineupLoading] = useState(false);
    const [allLineups, setAllLineups] = useState([]);
    const [filterStatus, setFilterStatus] = useState('all'); // all, upcoming, finished
    const [searchQuery, setSearchQuery] = useState('');
    // Fetch all lineups for a match
    const fetchLineups = async (matchId) => {
      setLineupLoading(true);
      try {
        const res = await apiService.admin.get(`/admin/match_lineups/${matchId}`);
        const rows = res.data || [];
        setAllLineups(rows);
        return rows;
      } catch {
        setAllLineups([]);
        return [];
      } finally {
        setLineupLoading(false);
      }
    };

    // Open lineup modal
    const openLineupModal = async (match, team) => {
      setLineupMatch(match);
      setLineupTeam(team);
      setShowLineupModal(true);
      setLineupPlayers([]);
      setLineupSubs([]);
      const rows = await fetchLineups(match.id);
      // Prefill jika sudah ada
      const found = (rows || []).find((l) => l.team_id === team.id || l.team_name === team.name);
      if (found) {
        setLineupPlayers(found.lineup || []);
        setLineupSubs(found.substitutes || []);
      } else {
        setLineupPlayers(team.players?.slice(0, 11) || []);
        setLineupSubs(team.players?.slice(11) || []);
      }
    };

    const normalizeLineupRows = (rows, teamRef, segment) => {
      const positionByIndex = ['GK', 'DEF', 'DEF', 'DEF', 'DEF', 'MID', 'MID', 'MID', 'FWD', 'FWD', 'FWD'];
      return (rows || [])
        .map((player, idx) => {
          const rawNumber = player?.number;
          const number = Number.isFinite(Number(rawNumber)) ? Number(rawNumber) : null;
          const name = (player?.name || '').toString().trim();
          const position = ((player?.position || '') || '').toString().trim().toUpperCase();
          if (!name) return null;
          return {
            player_id: player?.player_id || player?.id || `${teamRef?.id || teamRef?.name}-${segment}-${idx + 1}`,
            name,
            number: number ?? (segment === 'lineup' ? idx + 1 : 12 + idx),
            position: position || (segment === 'lineup' ? (positionByIndex[idx] || 'MID') : 'MID'),
          };
        })
        .filter(Boolean);
    };

    const closeLineupModal = () => {
      setShowLineupModal(false);
      setLineupMatch(null);
      setLineupTeam('');
      setLineupPlayers([]);
      setLineupSubs([]);
      setAllLineups([]);
    };

    const saveLineup = async () => {
      if (!lineupMatch || !lineupTeam) return;
      setLineupLoading(true);
      try {
        const payload = {
          match_id: lineupMatch.id,
          team_id: lineupTeam.id,
          team_name: lineupTeam.name,
          lineup: normalizeLineupRows(lineupPlayers, lineupTeam, 'lineup'),
          substitutes: normalizeLineupRows(lineupSubs, lineupTeam, 'subs'),
        };

        if (payload.lineup.length === 0) {
          toast({ title: 'Error', description: 'Starting XI belum diisi', variant: 'destructive' });
          return;
        }

        // Cek apakah sudah ada
        const found = (allLineups || []).find((l) => l.team_id === lineupTeam.id || l.team_name === lineupTeam.name);
        if (found) {
          await apiService.admin.put(`/admin/match_lineups/${found.id}`, payload);
        } else {
          await apiService.admin.post(`/admin/match_lineups`, payload);
        }
        toast({ title: 'Berhasil', description: 'Susunan pemain tersimpan' });
        await fetchLineups(lineupMatch.id);
        closeLineupModal();
      } catch (error) {
        const detail = error?.response?.data?.detail;
        toast({ title: 'Error', description: detail ? `Gagal simpan lineup: ${detail}` : 'Gagal simpan lineup', variant: 'destructive' });
      } finally {
        setLineupLoading(false);
      }
    };

    const exportLineupExcel = async (matchId) => {
      try {
        const token = localStorage.getItem('admin_token');
        const res = await fetch(`/api/admin/match_lineups/${matchId}/export_excel`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Export gagal');
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `susunan_pemain_${matchId}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        toast({ title: 'Error', description: 'Gagal export Excel', variant: 'destructive' });
      }
    };
  const [matches, setMatches] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [formData, setFormData] = useState({
    home_team: '',
    away_team: '',
    event_id: '',
    match_format: 'round_robin',
    home_leg_team1_score: '',
    home_leg_team2_score: '',
    away_leg_team1_score: '',
    away_leg_team2_score: '',
    date: new Date().toISOString().split('T')[0],
    time: '14:00',
    venue: '',
    status: 'scheduled',
    category: 'U-14'
  });

  // Goal events modal state
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [goalMatch, setGoalMatch] = useState(null);
  const [goalEvents, setGoalEvents] = useState([]);
  const [savingGoals, setSavingGoals] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importSheet, setImportSheet] = useState('');
  const [importCategory, setImportCategory] = useState('U-12');
  const [importStatus, setImportStatus] = useState('scheduled');
  const [importVenueDefault, setImportVenueDefault] = useState('');
  const [importing, setImporting] = useState(false);

  const { toast } = useToast();

  useEffect(() => {
    loadBootstrap();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadMatches(selectedEventId);
    } else {
      setMatches([]);
    }
  }, [selectedEventId]);

  useEffect(() => {
    const selectedEvent = events.find((ev) => ev.id === selectedEventId);
    if (!selectedEvent?.categories?.length) return;

    if (!selectedEvent.categories.includes(importCategory)) {
      setImportCategory(selectedEvent.categories[0]);
    }
  }, [events, selectedEventId, importCategory]);

  const loadBootstrap = async () => {
    try {
      const [participantsResponse, eventsResponse] = await Promise.all([
        apiService.admin.getParticipants(),
        apiService.admin.getEvents(),
      ]);
      setParticipants(participantsResponse.data || []);
      const eventRows = eventsResponse.data || [];
      setEvents(eventRows);
      if (!selectedEventId && eventRows.length > 0) {
        setSelectedEventId(eventRows[0].id);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal memuat data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadMatches = async (eventId) => {
    try {
      const matchesResponse = await apiService.admin.getMatches({ event_id: eventId, limit: 2000 });
      setMatches(matchesResponse.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal memuat pertandingan event', variant: 'destructive' });
      setMatches([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const toNullableInt = (value) => (value === '' ? null : parseInt(value, 10));
      const isRoundRobin = formData.match_format === 'round_robin';

      const homeLegTeam1 = toNullableInt(formData.home_leg_team1_score);
      const homeLegTeam2 = toNullableInt(formData.home_leg_team2_score);
      const awayLegTeam1 = toNullableInt(formData.away_leg_team1_score);
      const awayLegTeam2 = toNullableInt(formData.away_leg_team2_score);

      if (formData.status === 'finished') {
        if (isRoundRobin && (homeLegTeam1 === null || homeLegTeam2 === null)) {
          toast({ title: 'Error', description: 'Untuk round robin, isi Skor Kandang (Tim 1 dan Tim 2)', variant: 'destructive' });
          return;
        }
        if (!isRoundRobin && (homeLegTeam1 === null || homeLegTeam2 === null || awayLegTeam1 === null || awayLegTeam2 === null)) {
          toast({ title: 'Error', description: 'Untuk home away, isi Skor Kandang dan Skor Tandang (Tim 1 dan Tim 2)', variant: 'destructive' });
          return;
        }
      }

      const finalHomeScore = isRoundRobin
        ? homeLegTeam1
        : (homeLegTeam1 !== null && awayLegTeam1 !== null ? homeLegTeam1 + awayLegTeam1 : null);
      const finalAwayScore = isRoundRobin
        ? homeLegTeam2
        : (homeLegTeam2 !== null && awayLegTeam2 !== null ? homeLegTeam2 + awayLegTeam2 : null);

      const data = {
        ...formData,
        event_id: formData.event_id || selectedEventId || null,
        home_leg_team1_score: homeLegTeam1,
        home_leg_team2_score: homeLegTeam2,
        away_leg_team1_score: isRoundRobin ? null : awayLegTeam1,
        away_leg_team2_score: isRoundRobin ? null : awayLegTeam2,
        home_score: finalHomeScore,
        away_score: finalAwayScore,
        date: new Date(formData.date).toISOString()
      };

      if (editingMatch) {
        await apiService.admin.updateMatch(editingMatch.id, data);
        toast({ title: 'Berhasil', description: 'Tersimpan' });
      } else {
        await apiService.admin.createMatch(data);
        toast({ title: 'Berhasil', description: 'Tersimpan' });
      }
      
      await loadMatches(data.event_id || selectedEventId);
      closeModal();
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal menyimpan pertandingan', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus pertandingan ini?')) return;
    try {
      await apiService.admin.deleteMatch(id);
      toast({ title: 'Berhasil', description: 'Pertandingan berhasil dihapus' });
      await loadMatches(selectedEventId);
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal menghapus', variant: 'destructive' });
    }
  };

  // ---- Goal events helpers ----
  const openGoalModal = (match) => {
    setGoalMatch(match);
    setGoalEvents((match.goal_events || []).map((e) => ({
      player_name: e.player_name || '',
      team: e.team || 'home',
      minute: e.minute !== null && e.minute !== undefined ? String(e.minute) : '',
      assist_by: e.assist_by || 'N/A',
      type: e.type || 'goal',
    })));
    setShowGoalModal(true);
  };

  const closeGoalModal = () => {
    setShowGoalModal(false);
    setGoalMatch(null);
    setGoalEvents([]);
  };

  const updateGoalEvent = (index, field, value) => {
    setGoalEvents((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      if (field === 'team') {
        updated[index].player_name = '';
        updated[index].assist_by = 'N/A';
      }

      if (field === 'player_name' && updated[index].assist_by === value) {
        updated[index].assist_by = 'N/A';
      }

      return updated;
    });
  };

  const addGoalEvent = () => setGoalEvents((prev) => [...prev, emptyGoalEvent()]);

  const removeGoalEvent = (index) =>
    setGoalEvents((prev) => prev.filter((_, i) => i !== index));

  const getTeamPlayers = (teamName) => {
    if (!teamName) return [];
    const teamData = participants.find((p) => p.name === teamName);
    return (teamData?.players || [])
      .map((player) => (player?.name || '').trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'id'));
  };

  const getScorerOptions = (teamType) => {
    if (!goalMatch) return [];
    const teamName = teamType === 'home' ? goalMatch.home_team : goalMatch.away_team;
    return getTeamPlayers(teamName);
  };

  const getAssistOptions = (event) => {
    const scorerNames = new Set(
      goalEvents
        .map((goalEvent) => (goalEvent.player_name || '').trim())
        .filter(Boolean)
    );

    return getScorerOptions(event.team).filter((playerName) => !scorerNames.has(playerName));
  };

  const saveGoalEvents = async () => {
    setSavingGoals(true);
    try {
      const payload = goalEvents
        .filter((e) => e.player_name.trim())
        .map((e) => ({
          player_name: e.player_name.trim(),
          team: e.team,
          minute: e.minute !== '' ? parseInt(e.minute, 10) : null,
          assist_by: e.assist_by && e.assist_by !== 'N/A' ? e.assist_by.trim() : null,
          type: e.type,
        }));
      await apiService.admin.updateMatchGoals(goalMatch.id, { goal_events: payload });
      toast({ title: 'Berhasil', description: 'Detail pertandingan tersimpan' });
      await loadMatches(selectedEventId);
      closeGoalModal();
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal menyimpan detail', variant: 'destructive' });
    } finally {
      setSavingGoals(false);
    }
  };

  const openModal = (match = null) => {
    if (match) {
      setEditingMatch(match);
      setFormData({
        home_team: match.home_team,
        away_team: match.away_team,
        event_id: match.event_id || selectedEventId || '',
        match_format: match.match_format || ((match.away_leg_team1_score !== null && match.away_leg_team1_score !== undefined) || (match.away_leg_team2_score !== null && match.away_leg_team2_score !== undefined) ? 'home_away' : 'round_robin'),
        home_leg_team1_score: match.home_leg_team1_score ?? match.home_score ?? '',
        home_leg_team2_score: match.home_leg_team2_score ?? match.away_score ?? '',
        away_leg_team1_score: match.away_leg_team1_score ?? '',
        away_leg_team2_score: match.away_leg_team2_score ?? '',
        date: new Date(match.date).toISOString().split('T')[0],
        time: match.time,
        venue: match.venue,
        status: match.status,
        category: match.category
      });
    } else {
      setEditingMatch(null);
      const derivedMatchFormat = getEventMatchFormat(selectedEvent);
      const defaultCategory = selectedEvent?.categories?.[0] || 'U-14';
      setFormData({
        home_team: '',
        away_team: '',
        event_id: selectedEventId || '',
        match_format: derivedMatchFormat,
        home_leg_team1_score: '',
        home_leg_team2_score: '',
        away_leg_team1_score: '',
        away_leg_team2_score: '',
        date: new Date().toISOString().split('T')[0],
        time: '14:00',
        venue: '',
        status: 'scheduled',
        category: defaultCategory
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingMatch(null);
  };

  const getEventMatchFormat = (event) => {
    if (!event) return 'round_robin';
    if (event.format === 'league_round_robin' && event.league_type === 'home_away') {
      return 'home_away';
    }
    return 'round_robin';
  };

  const runImport = async (dryRun) => {
    if (!importFile) {
      toast({ title: 'Error', description: 'Pilih file Excel .xlsx dulu', variant: 'destructive' });
      return;
    }
    if (!selectedEventId) {
      toast({ title: 'Error', description: 'Pilih event tujuan import', variant: 'destructive' });
      return;
    }

    setImporting(true);
    try {
      const payload = new FormData();
      payload.append('file', importFile);
      payload.append('category', importCategory);
      payload.append('event_id', selectedEventId);
      payload.append('status', importStatus);
      payload.append('match_format', getEventMatchFormat(selectedEvent));
      payload.append('venue_default', importVenueDefault || '');
      payload.append('dry_run', String(dryRun));
      if (importSheet.trim()) {
        payload.append('sheet_name', importSheet.trim());
      }

      const response = await apiService.admin.importMatchesExcel(payload);
      const result = response.data || {};

      if (dryRun) {
        const firstErrors = (result.errors || []).slice(0, 3).map((er) => `baris ${er.row}: ${er.message}`).join(' | ');
        toast({
          title: 'Preview Import Selesai',
          description: `Parsed: ${result.parsed_rows || 0} baris. Error: ${(result.errors || []).length}.` +
            ((result.sample || []).length ? ` Contoh: ${(result.sample || []).slice(0, 2).map((s) => `${s.home_team} vs ${s.away_team}`).join(', ')}` : '') +
            ` | Peserta baru(${result.participant_target_type || '-'}) diperkirakan: ${result.participant_created || 0}, sudah ada: ${result.participant_existing || 0}` +
            (firstErrors ? ` | ${firstErrors}` : ''),
        });
      } else {
        const firstErrors = (result.errors || []).slice(0, 3).map((er) => `baris ${er.row}: ${er.message}`).join(' | ');
        toast({
          title: 'Import Berhasil',
          description: `Inserted: ${result.inserted_rows || 0}, duplikat dilewati: ${result.skipped_duplicates || 0}, error: ${(result.errors || []).length}.` +
            ` | Peserta baru(${result.participant_target_type || '-'}) dibuat: ${result.participant_created || 0}, sudah ada: ${result.participant_existing || 0}` +
            (firstErrors ? ` | ${firstErrors}` : ''),
        });
        await loadMatches(selectedEventId);
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Gagal import Excel jadwal', variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const toNullableInt = (value) => (value === '' ? null : parseInt(value, 10));
  const isRoundRobin = formData.match_format === 'round_robin';
  const selectedEvent = events.find((ev) => ev.id === selectedEventId) || null;
  const teamOptions = participants
    .filter((p) => p.category === formData.category)
    .filter((p) => {
      if (!selectedEvent?.event_target) return true;
      return (p.participant_type || 'school') === selectedEvent.event_target;
    })
    .map((p) => p.name)
    .sort((a, b) => a.localeCompare(b, 'id'));
  const awayTeamOptions = teamOptions.filter((name) => name !== formData.home_team);
  const previewHomeFinal = isRoundRobin
    ? toNullableInt(formData.home_leg_team1_score)
    : ((toNullableInt(formData.home_leg_team1_score) !== null && toNullableInt(formData.away_leg_team1_score) !== null)
      ? toNullableInt(formData.home_leg_team1_score) + toNullableInt(formData.away_leg_team1_score)
      : null);
  const previewAwayFinal = isRoundRobin
    ? toNullableInt(formData.home_leg_team2_score)
    : ((toNullableInt(formData.home_leg_team2_score) !== null && toNullableInt(formData.away_leg_team2_score) !== null)
      ? toNullableInt(formData.home_leg_team2_score) + toNullableInt(formData.away_leg_team2_score)
      : null);
  const displayedMatches = matches;
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredMatches = displayedMatches.filter((m) => {
    const matchesSearch = !normalizedSearch || [
      m.home_team,
      m.away_team,
      m.venue,
      m.category,
      m.status,
      m.time,
    ].some((value) => (value || '').toString().toLowerCase().includes(normalizedSearch));

    if (!matchesSearch) return false;
    if (filterStatus === 'all') return true;
    if (filterStatus === 'upcoming') return m.status !== 'finished';
    if (filterStatus === 'finished') return m.status === 'finished';
    return true;
  });

  return (
    <AdminLayout>
      <div className="flex gap-2 mb-4">
        <label>Status:
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="ml-2 px-2 py-1 border rounded">
            <option value="all">Semua Status</option>
            <option value="upcoming">Akan Datang</option>
            <option value="finished">Selesai</option>
          </select>
        </label>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full max-w-sm px-3 py-1 border rounded"
          placeholder="Cari tim, venue, kategori..."
        />
      </div>
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Kelola Pertandingan</h2>
          <p className="text-sm text-gray-600">Pertandingan di-generate dari menu Drawing</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <h3 className="text-lg font-semibold mb-3">Pilih Event</h3>
          <select
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="w-full md:w-1/2 px-3 py-2 border rounded-md"
          >
            <option value="">Pilih Event</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.name}</option>
            ))}
          </select>
          {selectedEvent && (
            <p className="text-xs text-gray-500 mt-2">
              Event aktif: {selectedEvent.name}. Daftar pertandingan di bawah akan terfilter untuk event ini.
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <h3 className="text-lg font-semibold mb-3">Import Jadwal dari Excel (.xlsx)</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              className="w-full px-3 py-2 border rounded-md"
            />
            <input
              type="text"
              value={importSheet}
              onChange={(e) => setImportSheet(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
              placeholder="Nama sheet (opsional)"
            />
            <input
              type="text"
              value={importVenueDefault}
              onChange={(e) => setImportVenueDefault(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
              placeholder="Venue default (opsional)"
            />
            <select value={importCategory} onChange={(e) => setImportCategory(e.target.value)} className="w-full px-3 py-2 border rounded-md">
              {(selectedEvent?.categories?.length ? selectedEvent.categories : ['U-12']).map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <input
              type="text"
              value={selectedEvent ? selectedEvent.name : 'Belum pilih event'}
              disabled
              className="w-full px-3 py-2 border rounded-md bg-gray-50 text-gray-600"
            />
            <select value={importStatus} onChange={(e) => setImportStatus(e.target.value)} className="w-full px-3 py-2 border rounded-md">
              <option value="scheduled">Dijadwalkan</option>
              <option value="finished">Selesai</option>
            </select>
            <input
              type="text"
              value={selectedEvent ? (getEventMatchFormat(selectedEvent) === 'home_away' ? 'Home Away (otomatis dari event)' : 'Round Robin (otomatis dari event)') : 'Pilih event dulu'}
              disabled
              className="w-full px-3 py-2 border rounded-md bg-gray-50 text-gray-600"
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Header Excel yang didukung fleksibel: home/tim 1, away/tim 2, tanggal/date, jam/time, venue/lapangan. Import akan masuk ke event yang sedang dipilih.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={() => runImport(true)}
              disabled={importing}
              className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
            >
              {importing ? 'Memproses...' : 'Preview Import'}
            </button>
            <button
              type="button"
              onClick={() => runImport(false)}
              disabled={importing}
              className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
            >
              {importing ? 'Memproses...' : 'Import ke Sistem'}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Memuat...</div>
        ) : !selectedEventId ? (
          <div className="text-center py-12 text-gray-500">Pilih event terlebih dahulu untuk melihat daftar pertandingan.</div>
        ) : filteredMatches.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {matches.length === 0
              ? 'Belum ada pertandingan untuk event ini.'
              : 'Pertandingan tidak ditemukan. Coba kata kunci atau filter lain.'}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredMatches.map((match) => {
              const isHomeAway = match.match_format === 'home_away' || match.away_leg_team1_score !== null || match.away_leg_team2_score !== null;
              const formatLabel = isHomeAway ? 'Home Away' : 'Round Robin';
              const showFinalScore = match.status === 'finished' && match.home_score !== null && match.away_score !== null;
              return (
                <div key={match.id} className="bg-white rounded-lg shadow-md p-4">
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">{match.home_team}</span>
                        <span className="mx-4 text-xl font-bold">{showFinalScore ? `${match.home_score} - ${match.away_score}` : 'VS'}</span>
                        <span className="font-semibold">{match.away_team}</span>
                      </div>
                      <p className="text-sm text-gray-600">{new Date(match.date).toLocaleDateString('id-ID')} • {match.time} • {match.venue}</p>
                      <div className="mt-2 flex items-center gap-2">
                        <span className={`inline-block px-2 py-1 text-xs rounded-full font-semibold ${match.status === 'finished' ? 'bg-gray-100 text-gray-800' : 'bg-blue-100 text-blue-800'}`}>
                          {match.status === 'finished' ? 'Selesai' : 'Akan Datang'}
                        </span>
                        <span className="inline-block px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 font-semibold">
                          {match.category}
                        </span>
                        <span className="inline-block px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                          {formatLabel}
                        </span>
                      </div>
                      {showFinalScore && (
                        <div className="mt-3 text-sm text-gray-700 space-y-1">
                          <p>
                            <span className="font-medium">Skor Akhir:</span> {match.home_score} - {match.away_score}
                          </p>
                          {isHomeAway && (
                            <>
                              <p>
                                <span className="font-medium">Skor Kandang:</span> {match.home_leg_team1_score ?? match.home_score} - {match.home_leg_team2_score ?? match.away_score}
                              </p>
                              <p>
                                <span className="font-medium">Skor Tandang:</span> {match.away_leg_team1_score ?? '-'} - {match.away_leg_team2_score ?? '-'}
                              </p>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2 ml-4">
                      {match.status === 'finished' && (
                        <button
                          onClick={() => openGoalModal(match)}
                          className="text-green-600 hover:text-green-800"
                          title="Detail Pertandingan"
                        >
                          <ClipboardList className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => openModal(match)} className="text-blue-600"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => handleDelete(match.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    {["home_team", "away_team"].map((side) => {
                      const team = participants.find((p) => p.name === match[side]);
                      if (!team) return null;
                      return (
                        <button
                          key={team.id}
                          className="px-2 py-1 rounded bg-blue-100 hover:bg-blue-200 text-blue-800 flex items-center gap-1"
                          onClick={() => openLineupModal(match, team)}
                          title={`Kelola Susunan Pemain ${team.name}`}
                        >
                          <Users2 size={16} /> Susunan {team.name}
                        </button>
                      );
                    })}
                    <button
                      className="px-2 py-1 rounded bg-green-100 hover:bg-green-200 text-green-800 flex items-center gap-1"
                      onClick={() => exportLineupExcel(match.id)}
                      title="Export Excel Susunan Pemain"
                    >
                      <ClipboardList size={16} /> Export Excel
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Modal Kelola Susunan Pemain */}
        {showLineupModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl relative">
              <button className="absolute top-2 right-2" onClick={closeLineupModal}><X size={20} /></button>
              <h3 className="text-lg font-bold mb-2">Susunan Pemain - {lineupTeam?.name}</h3>
              <div className="mb-2">
                <b>Starting XI:</b>
                <ul>
                  {lineupPlayers.map((p, idx) => (
                    <li key={idx} className="flex gap-2 items-center mb-1">
                      <input
                        className="border px-2 py-1 rounded w-8"
                        value={p.number || ''}
                        onChange={e => {
                          const val = e.target.value;
                          setLineupPlayers(lp => lp.map((pl, i) => i === idx ? { ...pl, number: val } : pl));
                        }}
                        placeholder="No"
                      />
                      <input
                        className="border px-2 py-1 rounded flex-1"
                        value={p.name || ''}
                        onChange={e => {
                          const val = e.target.value;
                          setLineupPlayers(lp => lp.map((pl, i) => i === idx ? { ...pl, name: val } : pl));
                        }}
                        placeholder="Nama Pemain"
                      />
                      <input
                        className="border px-2 py-1 rounded w-16"
                        value={p.position || ''}
                        onChange={e => {
                          const val = e.target.value;
                          setLineupPlayers(lp => lp.map((pl, i) => i === idx ? { ...pl, position: val } : pl));
                        }}
                        placeholder="Posisi"
                      />
                      <button onClick={() => setLineupPlayers(lp => lp.filter((_, i) => i !== idx))} className="text-red-500"><Trash2 size={16} /></button>
                    </li>
                  ))}
                </ul>
                <button className="mt-1 px-2 py-1 rounded bg-blue-50 hover:bg-blue-100 text-blue-700" onClick={() => setLineupPlayers(lp => [...lp, { name: '', number: '', position: '' }])}>+ Tambah Pemain</button>
              </div>
              <div className="mb-2">
                <b>Cadangan:</b>
                <ul>
                  {lineupSubs.map((p, idx) => (
                    <li key={idx} className="flex gap-2 items-center mb-1">
                      <input
                        className="border px-2 py-1 rounded w-8"
                        value={p.number || ''}
                        onChange={e => {
                          const val = e.target.value;
                          setLineupSubs(lp => lp.map((pl, i) => i === idx ? { ...pl, number: val } : pl));
                        }}
                        placeholder="No"
                      />
                      <input
                        className="border px-2 py-1 rounded flex-1"
                        value={p.name || ''}
                        onChange={e => {
                          const val = e.target.value;
                          setLineupSubs(lp => lp.map((pl, i) => i === idx ? { ...pl, name: val } : pl));
                        }}
                        placeholder="Nama Pemain"
                      />
                      <input
                        className="border px-2 py-1 rounded w-16"
                        value={p.position || ''}
                        onChange={e => {
                          const val = e.target.value;
                          setLineupSubs(lp => lp.map((pl, i) => i === idx ? { ...pl, position: val } : pl));
                        }}
                        placeholder="Posisi"
                      />
                      <button onClick={() => setLineupSubs(lp => lp.filter((_, i) => i !== idx))} className="text-red-500"><Trash2 size={16} /></button>
                    </li>
                  ))}
                </ul>
                <button className="mt-1 px-2 py-1 rounded bg-blue-50 hover:bg-blue-100 text-blue-700" onClick={() => setLineupSubs(lp => [...lp, { name: '', number: '', position: '' }])}>+ Tambah Cadangan</button>
              </div>
              <div className="flex gap-2 mt-4">
                <button className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700" onClick={saveLineup} disabled={lineupLoading}>Simpan</button>
                <button className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400" onClick={closeLineupModal}>Batal</button>
              </div>
            </div>
          </div>
        )}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4">{editingMatch ? 'Edit Pertandingan' : 'Tambah Pertandingan'}</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Tipe Pertandingan</label>
                  <input
                    type="text"
                    value={formData.match_format === 'home_away' ? 'Home Away' : 'Round Robin'}
                    disabled
                    className="w-full px-4 py-2 border rounded-md bg-gray-50 text-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Tim Kandang</label>
                  <select
                    value={formData.home_team}
                    onChange={(e) => {
                      const nextHomeTeam = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        home_team: nextHomeTeam,
                        away_team: prev.away_team === nextHomeTeam ? '' : prev.away_team,
                      }));
                    }}
                    className="w-full px-4 py-2 border rounded-md"
                    disabled={!!editingMatch}
                    required
                  >
                    <option value="">Pilih tim kandang</option>
                    {teamOptions.map((teamName) => (
                      <option key={teamName} value={teamName}>{teamName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Tim Tandang</label>
                  <select
                    value={formData.away_team}
                    onChange={(e) => setFormData({ ...formData, away_team: e.target.value })}
                    className="w-full px-4 py-2 border rounded-md"
                    disabled={!!editingMatch}
                    required
                  >
                    <option value="">Pilih tim tandang</option>
                    {awayTeamOptions.map((teamName) => (
                      <option key={teamName} value={teamName}>{teamName}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-3">
                  <label className="block text-sm font-medium">Skor Kandang</label>
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="number"
                      min="0"
                      placeholder="Tim 1"
                      value={formData.home_leg_team1_score}
                      onChange={(e) => setFormData({ ...formData, home_leg_team1_score: e.target.value })}
                      className="w-full px-4 py-2 border rounded-md"
                    />
                    <input
                      type="number"
                      min="0"
                      placeholder="Tim 2"
                      value={formData.home_leg_team2_score}
                      onChange={(e) => setFormData({ ...formData, home_leg_team2_score: e.target.value })}
                      className="w-full px-4 py-2 border rounded-md"
                    />
                  </div>
                </div>

                {!isRoundRobin && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium">Skor Tandang</label>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="number"
                        min="0"
                        placeholder="Tim 1"
                        value={formData.away_leg_team1_score}
                        onChange={(e) => setFormData({ ...formData, away_leg_team1_score: e.target.value })}
                        className="w-full px-4 py-2 border rounded-md"
                      />
                      <input
                        type="number"
                        min="0"
                        placeholder="Tim 2"
                        value={formData.away_leg_team2_score}
                        onChange={(e) => setFormData({ ...formData, away_leg_team2_score: e.target.value })}
                        className="w-full px-4 py-2 border rounded-md"
                      />
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 border rounded-md px-4 py-3">
                  <p className="text-sm text-gray-600">
                    Skor Akhir: <span className="font-semibold text-gray-900">{previewHomeFinal ?? '-'} - {previewAwayFinal ?? '-'}</span>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Tanggal</label>
                  <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="w-full px-4 py-2 border rounded-md" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Waktu</label>
                  <input type="time" value={formData.time} onChange={(e) => setFormData({ ...formData, time: e.target.value })} className="w-full px-4 py-2 border rounded-md" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Tempat</label>
                  <input type="text" value={formData.venue} onChange={(e) => setFormData({ ...formData, venue: e.target.value })} className="w-full px-4 py-2 border rounded-md" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full px-4 py-2 border rounded-md" required>
                    <option value="scheduled">Akan Datang</option>
                    <option value="finished">Selesai</option>
                  </select>
                </div>
                {editingMatch && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-2">
                    <p className="text-sm text-gray-600"><span className="font-semibold">Kategori:</span> {editingMatch.category}</p>
                    <p className="text-xs text-gray-500 mt-1">Kategori tidak dapat diubah (sudah ditentukan saat generate)</p>
                  </div>
                )}
                <div className="flex space-x-3 pt-4">
                  <button type="button" onClick={closeModal} className="flex-1 bg-gray-200 px-4 py-2 rounded-md">Batal</button>
                  <button type="submit" className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md">{editingMatch ? 'Update' : 'Simpan'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Goal Events Modal */}
        {showGoalModal && goalMatch && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">
                  Detail Pertandingan
                </h3>
                <button onClick={closeGoalModal} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-1">
                <span className="font-semibold">{goalMatch.home_team}</span>
                {' '}{goalMatch.home_score} – {goalMatch.away_score}{' '}
                <span className="font-semibold">{goalMatch.away_team}</span>
              </p>
              <p className="text-xs text-gray-400 mb-6">
                Masukkan pencetak gol dan assist. Kosongkan baris yang tidak perlu diisi.
              </p>

              {/* Goal event rows */}
              <div className="space-y-4">
                {goalEvents.map((event, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-gray-50 relative">
                    <button
                      onClick={() => removeGoalEvent(idx)}
                      className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2 sm:col-span-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Nama Pemain *
                        </label>
                        <select
                          value={event.player_name}
                          onChange={(e) => updateGoalEvent(idx, 'player_name', e.target.value)}
                          className="w-full px-3 py-1.5 border rounded-md text-sm"
                        >
                          <option value="">Pilih pencetak gol</option>
                          {getScorerOptions(event.team).map((playerName) => (
                            <option key={`${event.team}-${playerName}`} value={playerName}>
                              {playerName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Tim</label>
                        <select
                          value={event.team}
                          onChange={(e) => updateGoalEvent(idx, 'team', e.target.value)}
                          className="w-full px-3 py-1.5 border rounded-md text-sm"
                        >
                          <option value="home">Kandang ({goalMatch.home_team})</option>
                          <option value="away">Tandang ({goalMatch.away_team})</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Tipe
                        </label>
                        <select
                          value={event.type}
                          onChange={(e) => updateGoalEvent(idx, 'type', e.target.value)}
                          className="w-full px-3 py-1.5 border rounded-md text-sm"
                        >
                          <option value="goal">Gol</option>
                          <option value="own_goal">Gol Bunuh Diri</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Menit (opsional)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="120"
                          value={event.minute}
                          onChange={(e) => updateGoalEvent(idx, 'minute', e.target.value)}
                          placeholder="e.g. 35"
                          className="w-full px-3 py-1.5 border rounded-md text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Assist oleh (opsional)
                        </label>
                        <select
                          value={event.assist_by}
                          onChange={(e) => updateGoalEvent(idx, 'assist_by', e.target.value)}
                          className="w-full px-3 py-1.5 border rounded-md text-sm"
                        >
                          <option value="N/A">N/A</option>
                          {getAssistOptions(event).map((playerName) => (
                            <option key={`assist-${event.team}-${playerName}`} value={playerName}>
                              {playerName}
                            </option>
                          ))}
                          {event.assist_by &&
                            event.assist_by !== 'N/A' &&
                            !getAssistOptions(event).includes(event.assist_by) && (
                              <option value={event.assist_by}>{event.assist_by}</option>
                            )}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={addGoalEvent}
                className="mt-4 w-full border-2 border-dashed border-green-300 text-green-600 hover:border-green-500 hover:text-green-700 py-2 rounded-lg text-sm font-medium"
              >
                + Tambah Gol
              </button>

              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeGoalModal}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-md text-sm"
                >
                  Batal
                </button>
                <button
                  onClick={saveGoalEvents}
                  disabled={savingGoals}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white px-4 py-2 rounded-md text-sm"
                >
                  {savingGoals ? 'Menyimpan...' : 'Simpan Detail'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMatches;